import React from "react";

import { Section } from "../PatternSections";
import { ContextGrid, ContextList, FirstQueryQuote, GridSkeleton, ManualSource, QuoteSkeleton, contextCells } from "./ContextBlocks";
import useSessionContext, { CONTEXT_STATUS } from "./useSessionContext";
import { SESSION_CONTEXT_TEXT } from "./catalogText";

const T = SESSION_CONTEXT_TEXT;

/** The grid and the entries it does not hold; `empty` (or nothing) when the conversation has no context. */
function ContextBody({ data, empty }) {
  const { cells, rest } = contextCells(data);
  if (!cells.length && !rest.length) return empty ? <p className="le-muted">{empty}</p> : null;
  return (
    <>
      {cells.length > 0 && <ContextGrid cells={cells} label={T.context} />}
      {rest.length > 0 && <ContextList entries={rest} />}
    </>
  );
}

/** A loaded conversation. An auto entry always shows both sections (with their empty states); a Manual entry only what its
 * chat recorded, since its source block already says where it came from. */
function Ready({ data, manual }) {
  const text = data.firstQuery && data.firstQuery.text;
  const context = <ContextBody data={data} empty={manual ? null : T.noContext} />;
  return (
    <>
      {(text || !manual) && (
        <Section label={T.firstQuery}>{text ? <FirstQueryQuote text={text} /> : <p className="le-muted">{T.noFirstQuery}</p>}</Section>
      )}
      {manual ? context : <Section label={T.context}>{context}</Section>}
    </>
  );
}

/**
 * The top of a detail popup. An auto (conversation-derived) entry: the engineer's first message as a quote (Copy, Show more
 * for long text) and a compact context grid (product, problem area, engineer, start, messages, status chip) with the other
 * context entries under it, in the server's labels. A Manual entry (`manual` = {savedBy, savedAt, category, caseId}) starts
 * with a "Taught manually" source block instead of an empty first query, then whatever its chat recorded. Loads when the
 * popup opens (useSessionContext), or shows the `context` the popup already loaded (one request shared with the diagnosis
 * figures); while it loads, skeletons keep the final geometry. A gone conversation, another engineer's conversation, a store
 * that is not connected and a failure (with Retry) each say so in place of the conversation. Nothing is shown for an item
 * with neither a session nor a manual source.
 */
export default function SessionContext({ sessionId, context, manual }) {
  const own = useSessionContext(context ? "" : sessionId);
  const { status, data, retry } = context || own;
  if (status === CONTEXT_STATUS.NONE && !manual) return null;

  let body = null;
  if (status === CONTEXT_STATUS.LOADING) {
    body = manual ? <><QuoteSkeleton /><GridSkeleton /></> : (
      <>
        <Section label={T.firstQuery}><QuoteSkeleton /></Section>
        <Section label={T.context}><GridSkeleton /></Section>
      </>
    );
  } else if (status === CONTEXT_STATUS.READY) {
    body = <Ready data={data} manual={!!manual} />;
  } else if (status === CONTEXT_STATUS.GONE) {
    body = <p className="le-note le-note--blocked">{T.gone}</p>;
  } else if (status === CONTEXT_STATUS.FORBIDDEN) {
    body = <p className="le-note le-note--blocked">{T.forbidden}</p>;
  } else if (status === CONTEXT_STATUS.NOT_CONNECTED) {
    body = <p className="le-muted">{T.notConnected}</p>;
  } else if (status === CONTEXT_STATUS.FAILED) {
    body = (
      <div className="le-actions">
        <p className="le-error" role="alert">{T.failed}</p>
        <button type="button" className="le-btn le-btn--sm" onClick={retry}>{T.retry}</button>
      </div>
    );
  }
  return (
    <div className="le-fields" role="group" aria-label={T.label} aria-busy={status === CONTEXT_STATUS.LOADING}>
      {status === CONTEXT_STATUS.LOADING && <span className="sr-only" role="status">{T.loading}</span>}
      {manual && <ManualSource manual={manual} />}
      {body}
    </div>
  );
}
